import { useState, useEffect, useCallback } from 'react';
import { assetsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import SlideOver from '../../../components/ui/SlideOver';
import { Plus, Archive, RotateCcw, Edit, History } from 'lucide-react';
import {
  HISTORY_EVENT_TYPES,
  DOCUMENT_TYPES,
  labelFor,
  formatAssetDate,
  formatAssetCurrency,
} from '../../../lib/assetConstants';
import { useToast } from '../../../hooks/useToast';

const selectClass =
  'flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const emptyEventForm = () => ({
  eventType: 'service',
  eventDate: new Date().toISOString().slice(0, 10),
  cost: '',
  supplierName: '',
  serviceProvider: '',
  notes: '',
  documentType: 'service_report',
});

const AssetHistoryPanel = ({ assetId }) => {
  const { addToast } = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyEventForm());
  const [attachFile, setAttachFile] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState(emptyEventForm());

  const fetchHistory = useCallback(async () => {
    if (!assetId) return;
    try {
      setLoading(true);
      const res = await assetsAPI.getHistory(assetId, {
        includeArchived: includeArchived || undefined,
      });
      setHistory(res.history || []);
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to load history' });
    } finally {
      setLoading(false);
    }
  }, [assetId, includeArchived, addToast]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await assetsAPI.createHistory(
        assetId,
        {
          eventType: form.eventType,
          eventDate: form.eventDate,
          cost: form.cost !== '' ? form.cost : undefined,
          supplierName: form.supplierName.trim() || undefined,
          serviceProvider: form.serviceProvider.trim() || undefined,
          notes: form.notes.trim() || undefined,
          documentType: attachFile ? form.documentType : undefined,
        },
        attachFile
      );
      addToast({ type: 'success', message: 'History event added' });
      setShowAddForm(false);
      setForm(emptyEventForm());
      setAttachFile(null);
      fetchHistory();
    } catch (err) {
      addToast({
        type: 'error',
        message: err.response?.data?.error || err.message || 'Failed to add event',
      });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setEditForm({
      eventType: event.eventType,
      eventDate: event.eventDate?.slice?.(0, 10) || event.eventDate || '',
      cost: event.cost ?? '',
      supplierName: event.supplierName || '',
      serviceProvider: event.serviceProvider || '',
      notes: event.notes || '',
      documentType: 'service_report',
    });
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    try {
      setSaving(true);
      await assetsAPI.updateHistory(assetId, editingEvent.id, {
        eventType: editForm.eventType,
        eventDate: editForm.eventDate,
        cost: editForm.cost !== '' ? editForm.cost : null,
        supplierName: editForm.supplierName.trim() || null,
        serviceProvider: editForm.serviceProvider.trim() || null,
        notes: editForm.notes.trim() || null,
      });
      addToast({ type: 'success', message: 'History event updated' });
      setEditingEvent(null);
      fetchHistory();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to update event' });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (eventId) => {
    if (!confirm('Archive this history event?')) return;
    try {
      await assetsAPI.archiveHistory(assetId, eventId);
      addToast({ type: 'success', message: 'Event archived' });
      fetchHistory();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to archive' });
    }
  };

  const handleRestore = async (eventId) => {
    try {
      await assetsAPI.restoreHistory(assetId, eventId);
      addToast({ type: 'success', message: 'Event restored' });
      fetchHistory();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to restore' });
    }
  };

  const EventFormFields = ({ data, onChange, showFile }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">Event type</FormLabel>
          <select
            value={data.eventType}
            onChange={(e) => onChange({ ...data, eventType: e.target.value })}
            className={selectClass}
            required
          >
            {HISTORY_EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <FormLabel required className="text-gray-700 dark:text-gray-300">Event date</FormLabel>
          <Input
            type="date"
            value={data.eventDate}
            onChange={(e) => onChange({ ...data, eventDate: e.target.value })}
            required
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Cost (£)</FormLabel>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={data.cost}
            onChange={(e) => onChange({ ...data, cost: e.target.value })}
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Supplier</FormLabel>
          <Input
            value={data.supplierName}
            onChange={(e) => onChange({ ...data, supplierName: e.target.value })}
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="md:col-span-2">
          <FormLabel className="text-gray-700 dark:text-gray-300">Service provider</FormLabel>
          <Input
            value={data.serviceProvider}
            onChange={(e) => onChange({ ...data, serviceProvider: e.target.value })}
            className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>
      <div>
        <FormLabel className="text-gray-700 dark:text-gray-300">Notes</FormLabel>
        <Textarea
          value={data.notes}
          onChange={(e) => onChange({ ...data, notes: e.target.value })}
          rows={3}
          className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
      </div>
      {showFile && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-200 dark:border-gray-700 pt-4">
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Attach certificate / document</FormLabel>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-700 dark:text-gray-300"
            />
          </div>
          {attachFile && (
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Document type</FormLabel>
              <select
                value={data.documentType}
                onChange={(e) => onChange({ ...data, documentType: e.target.value })}
                className={selectClass}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <History className="h-4 w-4" />
          Service & maintenance history
        </h3>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Show archived
          </label>
          <Button variant="primary" size="sm" onClick={() => setShowAddForm(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Add event
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden bg-white dark:bg-gray-800">
        {loading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading history…</div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No history events yet. Record calibrations, repairs, and inspections here.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((event) => (
                <TableRow key={event.id} className={event.isArchived ? 'opacity-60' : ''}>
                  <TableCell className="text-sm whitespace-nowrap">{formatAssetDate(event.eventDate)}</TableCell>
                  <TableCell className="text-sm">{labelFor(HISTORY_EVENT_TYPES, event.eventType)}</TableCell>
                  <TableCell className="text-sm">
                    {event.serviceProvider || event.supplierName || '—'}
                  </TableCell>
                  <TableCell className="text-sm text-right">{formatAssetCurrency(event.cost)}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate" title={event.notes || ''}>
                    {event.notes || '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!event.isArchived && (
                        <>
                          <Button variant="icon" size="sm" onClick={() => openEdit(event)} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="icon" size="sm" onClick={() => handleArchive(event.id)} title="Archive">
                            <Archive className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {event.isArchived && (
                        <Button variant="icon" size="sm" onClick={() => handleRestore(event.id)} title="Restore">
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <SlideOver
        open={showAddForm}
        onOpenChange={setShowAddForm}
        title="Add history event"
        width="w-full max-w-2xl"
      >
        <form onSubmit={handleAdd}>
          <EventFormFields data={form} onChange={setForm} showFile />
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Add event'}
            </Button>
          </div>
        </form>
      </SlideOver>

      <SlideOver
        open={!!editingEvent}
        onOpenChange={(open) => { if (!open) setEditingEvent(null); }}
        title="Edit history event"
        width="w-full max-w-2xl"
      >
        <form onSubmit={handleEditSave}>
          <EventFormFields data={editForm} onChange={setEditForm} showFile={false} />
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={() => setEditingEvent(null)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </SlideOver>
    </div>
  );
};

export default AssetHistoryPanel;
